'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/Sheet';
import { useTabContext } from '../context/TabContext';
import { customerMenuItemsMobile, menuItemsMobile } from '@/constant/navigation';
import { CustomButton } from '@/components/ui/CustomButton';
import { usePathname } from 'next/navigation';
import { validLifetimeMortgagePaths } from '@/components/layout/DesktopMenu';

const MobileMenu = () => {
  const { selectedTab } = useTabContext();
  const [isOpen, setIsOpen] = useState(false);
  const [side, setSide] = useState('center');
  const menuItemsToShow = selectedTab === 'adviser' ? menuItemsMobile : customerMenuItemsMobile;
  const [openItem, setOpenItem] = useState(null);
  const pathname = usePathname();
  const isLifetimeMortgages = validLifetimeMortgagePaths.includes(pathname);
  const logo = isLifetimeMortgages ? '/img/logo-2.svg' : '/img/logo.svg';
  const handleAccordionChange = (value) => {
    setOpenItem(value);
  };
  const handleSheetOpenChange = (isOpen) => {
    setIsOpen(isOpen);
    if (!isOpen) {
      setOpenItem(null);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSide('right');
        setIsOpen(false);
      } else {
        setSide('center');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={`lg:hidden fixed top-[39px] w-full ${isLifetimeMortgages ? 'bg-[#0A1628]' : 'bg-white'}`}>
      <div className="container mx-auto flex items-center justify-between py-6">
        <Link href={selectedTab === 'adviser' ? '/professionals' : '/'}>
          <img src={logo} alt="Logo" />
        </Link>
        <Sheet open={isOpen} onOpenChange={handleSheetOpenChange}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-[0.5rem] border-2 border-[#CBCDD6] w-[2.5rem] h-[2.5rem] bg-[#FDFDFF] mt-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path d="M2 8.53369H14M2 4.53369H14M6 12.5337H14" stroke={`${selectedTab === 'adviser' ? '#006EB7' : '#A51367'}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="sr-only">Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side={side}
            className={`w-[94%] max-w-md p-0 bg-[#FDFDFF] rounded-lg m-4 z-40 ${
              side === 'center' ? 'left-0 right-0 mx-auto' : 'md:h-[95%]'
            }`}
            selectedTab={selectedTab}
          >
            <SheetHeader className="p-[1.75rem_1.5rem]">
              <SheetTitle>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {selectedTab === 'adviser'
                      ? <>
                        <Link href="https://www.linkedin.com/company/pure-retirement-ltd">
                          <img src="/img/icons/linkedin-dark.svg" className="w-5 h-5" alt="LinkedIn" />
                        </Link>

                        <Link href="https://www.youtube.com/channel/UCB9qhCS5ZWArD1hiZF0P48g">
                          <img src="/img/icons/youtube-dark.svg" className="w-5 h-5" alt="YouTube" />
                        </Link>
                      </>

                      : <Link href="https://www.youtube.com/@pureretirementforourcustom6170/videos">
                        <img src="/img/icons/youtube-customer.svg" className="w-5 h-5" alt="YouTube" />
                      </Link>
                    }
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col">
              <div className="flex-1 overflow-auto">
                <Accordion type="single" collapsible onValueChange={handleAccordionChange}>
                  {menuItemsToShow.map((item) =>
                    item.submenu ? (
                      <AccordionItem
                        key={item.title}
                        value={item.title}
                        className="border-b"
                      >
                        <AccordionTrigger
                          className={`${
                            selectedTab === 'adviser'
                              ? 'hover:menu-item-open'
                              : 'hover:customer-menu-item-open'
                          } flex justify-between w-full px-5 py-3 menu-item-text ${
                            openItem === item.title ? 'bg-[#F3F4F8]' : ''
                          }`}
                          isMobile={true}
                        >
                          {item.title}
                        </AccordionTrigger>
                        {/* eslint-disable-next-line max-len */}
                        <AccordionContent
                          className={`pl-7 ${openItem === item.title ? 'bg-[#F3F4F8]' : 'bg-[#FDFDFF]'}`}>
                          {item.submenu.map((subItem) => (
                            <Link href={subItem.href} key={subItem.title} onClick={() => setIsOpen(false)}>
                              <p className={`${
                                selectedTab === 'adviser' ? 'hover:menu-item-open' : 'hover:customer-menu-item-open'
                              } menu-item-link cursor-pointer pb-4`}>{subItem.title}</p>
                            </Link>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <Link
                        key={item.title}
                        href="#"
                        className="block px-4 py-3 text-gray-700 border-b hover:bg-gray-100"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.title}
                      </Link>
                    )
                  )}
                </Accordion>
              </div>

              {selectedTab === 'adviser'
                ? <div className="p-4 mt-28 md:mt-24">
                  <div className="flex flex-col space-y-3">
                    <a href="https://adviserportal.pureretirement.co.uk/login" target="_blank">
                      <CustomButton variant="secondaryLarge" className="w-full sm:w-full">
                        Login
                      </CustomButton>
                    </a>
                    <a href="/professionals/register" target="_blank">
                      <CustomButton variant="primaryLarge" className="w-full sm:w-full"
                      >
                        Register
                      </CustomButton>
                    </a>
                  </div>
                </div>

                : <div className="p-4 mt-28 md:mt-24">
                  <a href="https://customer.pureretirement.co.uk" target="_blank">
                    <CustomButton variant="primaryMagentaLarge" className="w-full sm:w-full">
                    MyPure Login
                    </CustomButton>
                  </a>
                </div>
              }
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default MobileMenu;
